import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, BarChart3, Eye, EyeOff } from 'lucide-react';

const LegacyScoreOverTime = ({ h2hData = [], finalData = [], legacyData = [], yearRange = [2012, 2024] }) => {
  const [chartData, setChartData] = useState([]);
  const [visibleManagers, setVisibleManagers] = useState(new Set());
  const [highlightedManager, setHighlightedManager] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const managers = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott', 'Sam'];
  
  // Modern color palette for manager lines
  const managerColors = {
    'Kevin': '#10b981', // Emerald
    'Jake': '#f59e0b', // Amber
    'Lyons': '#22d3ee', // Cyan
    'Nipples': '#06b6d4', // Sky
    'Dan': '#ef4444', // Red
    'Shuds': '#8b5cf6', // Violet
    'Karp': '#84cc16', // Lime
    'Mills': '#14b8a6', // Teal
    'Joey': '#eab308', // Yellow
    'Raffy': '#ec4899', // Pink
    'Oster': '#3b82f6', // Blue
    'Scott': '#f97316', // Orange
    'Sam': '#6366f1'   // Indigo
  };

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // FIXED: Weekly ranking calculation that exactly matches LegacyScoreFactors
  const calculateWeeklyPositions = (regSeasonGames, manager = '') => {
    const weeklyPositions = {
      first: 0, twelfth: 0, second: 0, eleventh: 0, third: 0, tenth: 0,
      fourth: 0, ninth: 0, fifth: 0, eighth: 0, sixth: 0, seventh: 0
    };

    regSeasonGames.forEach(game => {
      const winsThisWeek = game['Wins (This Week)'] || 0;
      const lossesThisWeek = game['Losses (This Week)'] || 0;
      
      // IMPORTANT: Validate data integrity
      if (winsThisWeek + lossesThisWeek !== 11) {
        console.warn('Invalid weekly ranking data for', manager, ':', {
          year: game.Year,
          week: game.Week,
          manager: game.Manager1,
          wins: winsThisWeek,
          losses: lossesThisWeek,
          total: winsThisWeek + lossesThisWeek
        });
        return; // Skip invalid records
      }
      
      // FIXED: Weekly position logic - count ALL instances, not just wins/losses
      // This EXACTLY matches the logic in LegacyScoreFactors.js
      
      // 1st place: Beat 11 teams (11 wins, 0 losses)
      if (winsThisWeek === 11) weeklyPositions.first += 1;
      
      // 12th place: Beat 0 teams (0 wins, 11 losses)  
      if (winsThisWeek === 0) weeklyPositions.twelfth += 1;
      
      // 2nd place: Beat 10 teams (10 wins, 1 loss)
      if (winsThisWeek === 10) weeklyPositions.second += 1;
      
      // 11th place: Beat 1 team (1 win, 10 losses)
      if (winsThisWeek === 1) weeklyPositions.eleventh += 1;
      
      // 3rd place: Beat 9 teams (9 wins, 2 losses)
      if (winsThisWeek === 9) weeklyPositions.third += 1;
      
      // 10th place: Beat 2 teams (2 wins, 9 losses)
      if (winsThisWeek === 2) weeklyPositions.tenth += 1;
      
      // 4th place: Beat 8 teams (8 wins, 3 losses)
      if (winsThisWeek === 8) weeklyPositions.fourth += 1;
      
      // 9th place: Beat 3 teams (3 wins, 8 losses)
      if (winsThisWeek === 3) weeklyPositions.ninth += 1;
      
      // 5th place: Beat 7 teams (7 wins, 4 losses)
      if (winsThisWeek === 7) weeklyPositions.fifth += 1;
      
      // 8th place: Beat 4 teams (4 wins, 7 losses)  
      if (winsThisWeek === 4) weeklyPositions.eighth += 1;
      
      // 6th place: Beat 6 teams (6 wins, 5 losses)
      if (winsThisWeek === 6) weeklyPositions.sixth += 1;
      
      // 7th place: Beat 5 teams (5 wins, 6 losses)
      if (winsThisWeek === 5) weeklyPositions.seventh += 1;
    });

    return weeklyPositions;
  };

  // FIXED: Calculate legacy score for a specific manager up to a specific year - EXACTLY like LegacyScoreFactors
  const calculateLegacyScoreUpToYear = (manager, upToYear) => {
    if (!h2hData.length || !finalData.length) return 0;

    // Filter data for this manager and up to the specified year
    const managerH2H = h2hData.filter(game => 
      game.Manager1 === manager && 
      game.Year >= yearRange[0] && 
      game.Year <= upToYear
    );

    const managerSeasons = finalData.filter(season => 
      season.Owner === manager &&
      season.Year >= yearRange[0] && 
      season.Year <= upToYear
    );

    // Regular season wins/losses
    const regSeasonGames = managerH2H.filter(game => game.Type === 'Regular Season');
    const regWins = regSeasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
    const regLosses = regSeasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);

    // FIXED: Use the corrected weekly position calculation
    const weeklyPositions = calculateWeeklyPositions(regSeasonGames, manager);

    // Playoff wins
    const playoffGames = managerH2H.filter(game => game.Type === 'Playoffs');
    const playoffWins = playoffGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);

    // Standings finishes - EXACTLY like LegacyScoreFactors
    const standingsFinishes = Array.from({length: 12}, () => 0);
    managerSeasons.forEach(season => {
      if (season.Standings >= 1 && season.Standings <= 12) {
        standingsFinishes[season.Standings - 1]++;
      }
    });

    // Annual scoring race - calculate top and bottom scorers by season - EXACTLY like LegacyScoreFactors
    let scoreChamp = 0;
    let scoreLoser = 0;
    
    // For each season in the range up to upToYear, find who had highest/lowest PF
    for (let year = yearRange[0]; year <= upToYear; year++) {
      const seasonData = finalData.filter(season => season.Year === year);
      
      if (seasonData.length > 0) {
        // Find highest and lowest PF (Points For) in this season
        const maxPF = Math.max(...seasonData.map(s => s.PF || 0));
        const minPF = Math.min(...seasonData.map(s => s.PF || 0));
        
        // Check if this manager had the highest PF
        const managerSeason = seasonData.find(s => s.Owner === manager);
        if (managerSeason && managerSeason.PF === maxPF) {
          scoreChamp++;
        }
        
        // Check if this manager had the lowest PF
        if (managerSeason && managerSeason.PF === minPF) {
          scoreLoser++;
        }
      }
    }

    // FIXED: Calculate total legacy score exactly like LegacyScoreFactors
    const totalScore = regWins * 5 + regLosses * -5 +
                      weeklyPositions.first * 15 + weeklyPositions.twelfth * -15 +
                      weeklyPositions.second * 5 + weeklyPositions.eleventh * -5 +
                      weeklyPositions.third * 4 + weeklyPositions.tenth * -4 +
                      weeklyPositions.fourth * 3 + weeklyPositions.ninth * -3 +
                      weeklyPositions.fifth * 2 + weeklyPositions.eighth * -2 +
                      weeklyPositions.sixth * 1 + weeklyPositions.seventh * -1 +
                      playoffWins * 10 +
                      standingsFinishes[0] * 125 + standingsFinishes[1] * 75 + standingsFinishes[2] * 60 +
                      standingsFinishes[3] * 40 + standingsFinishes[4] * 30 + standingsFinishes[5] * 15 +
                      standingsFinishes[6] * -15 + standingsFinishes[7] * -30 + standingsFinishes[8] * -40 +
                      standingsFinishes[9] * -50 + standingsFinishes[10] * -60 + standingsFinishes[11] * -80 +
                      scoreChamp * 30 + scoreLoser * -30;

    return Math.round(totalScore);
  };

  // FIXED: Prepare chart data by calculating legacy scores for each year using our corrected calculation
  const prepareLegacyChartData = () => {
    if (!h2hData.length || !finalData.length) return [];

    // Get all years in the range
    const years = [];
    for (let year = yearRange[0]; year <= yearRange[1]; year++) {
      years.push(year);
    }

    // Create chart data structure
    const chartData = years.map(year => {
      const yearData = { year };
      
      // Calculate cumulative legacy score for each manager up to this year
      managers.forEach(manager => {
        yearData[manager] = calculateLegacyScoreUpToYear(manager, year);
      });
      
      return yearData;
    });

    return chartData;
  };

  // Toggle manager visibility
  const toggleManager = (manager) => {
    const newVisible = new Set(visibleManagers);
    if (newVisible.has(manager)) {
      newVisible.delete(manager);
    } else {
      newVisible.add(manager);
    }
    setVisibleManagers(newVisible);
  };

  // Show/hide all managers
  const toggleAllManagers = () => {
    if (visibleManagers.size === managers.length) {
      setVisibleManagers(new Set());
    } else {
      setVisibleManagers(new Set(managers));
    }
  };

  // Custom tooltip with mobile optimization
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Get all managers' data for this year, not just visible ones
      const allManagerData = managers.map(manager => {
        const dataPoint = payload.find(p => p.dataKey === manager);
        return {
          name: manager,
          value: dataPoint ? dataPoint.value : 0,
          color: managerColors[manager],
          isVisible: visibleManagers.has(manager)
        };
      }).sort((a, b) => b.value - a.value); // Sort by score descending

      const maxItems = isMobile ? 6 : 13; // Show fewer on mobile

      return (
        <div className={`bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-2xl p-3 sm:p-4 ${
          isMobile ? 'w-64' : 'w-72'
        }`}>
          <p className={`font-bold text-gray-900 mb-2 sm:mb-3 text-center border-b border-gray-200 pb-2 ${
            isMobile ? 'text-sm' : 'text-base'
          }`}>
            {label} (Cumulative)
          </p>
          <div className={`space-y-1 max-h-64 sm:max-h-96 overflow-y-auto ${
            isMobile ? 'text-xs' : 'text-sm'
          }`}>
            {allManagerData.slice(0, maxItems).map((entry, index) => (
              <div key={entry.name} className="flex items-center justify-between min-w-0">
                <div className="flex items-center min-w-0 flex-1">
                  <div 
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 flex-shrink-0 ${
                      entry.isVisible ? '' : 'opacity-40'
                    }`}
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className={`font-medium truncate ${
                    entry.isVisible ? 'text-gray-700' : 'text-gray-400'
                  }`}>
                    {entry.name}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">
                    #{index + 1}
                  </span>
                </div>
                <span className={`font-bold ml-3 flex-shrink-0 ${
                  entry.value >= 0 ? 'text-green-600' : 'text-red-600'
                } ${entry.isVisible ? '' : 'opacity-60'}`}>
                  {entry.value >= 0 ? '+' : ''}{entry.value}
                </span>
              </div>
            ))}
            {allManagerData.length > maxItems && (
              <div className="text-center text-gray-400 pt-1">
                +{allManagerData.length - maxItems} more
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom dot for highlighting
  const CustomDot = ({ cx, cy, payload, dataKey }) => {
    if (highlightedManager === dataKey) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={isMobile ? 4 : 6}
          fill={managerColors[dataKey]}
          stroke="white"
          strokeWidth={isMobile ? 2 : 3}
          className="animate-pulse"
        />
      );
    }
    return null;
  };

  useEffect(() => {
    if (h2hData.length && finalData.length) {
      const data = prepareLegacyChartData();
      setChartData(data);
      
      // Show top 5 managers by default on mobile, all on desktop
      if (isMobile) {
        const finalYear = data[data.length - 1];
        if (finalYear) {
          const topManagers = Object.entries(finalYear)
            .filter(([key]) => key !== 'year')
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5)
            .map(([manager]) => manager);
          setVisibleManagers(new Set(topManagers));
        }
      } else {
        setVisibleManagers(new Set(managers));
      }
      
      // Debug output for verification
      if (data.length > 0) {
        const finalYear = data[data.length - 1];
        console.log('=== LEGACY SCORE OVER TIME DEBUG (FIXED) ===');
        console.log('Final year data:', finalYear);
        console.log('Kevin final score:', finalYear.Kevin);
        console.log('Should match LegacyScoreFactors total');
      }
    }
  }, [h2hData, finalData, yearRange, isMobile]);

  if (!chartData.length) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm sm:text-base">Calculating legacy score progression...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
      {/* Header with mobile optimization */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0 flex-1">
            <TrendingUp className="h-5 w-5 sm:h-7 sm:w-7 mr-2 sm:mr-3 text-white flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold text-white truncate">
                {isMobile ? 'Legacy Score Over Time' : 'Legacy Score Over Time'}
              </h2>
              <p className="text-purple-100 text-xs sm:text-sm">
                {isMobile 
                  ? `Cumulative scores (${yearRange[0]}-${yearRange[1]})`
                  : `Cumulative legacy score progression by manager (${yearRange[0]}-${yearRange[1]})`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <button
              onClick={toggleAllManagers}
              className="flex items-center space-x-1 sm:space-x-2 bg-white/20 hover:bg-white/30 text-white px-2 sm:px-3 py-1 sm:py-2 rounded-lg transition-colors duration-200"
            >
              {visibleManagers.size === managers.length ? (
                <><EyeOff className="h-3 w-3 sm:h-4 sm:w-4" /><span className="text-xs sm:text-sm hidden sm:inline">Hide All</span></>
              ) : (
                <><Eye className="h-3 w-3 sm:h-4 sm:w-4" /><span className="text-xs sm:text-sm hidden sm:inline">Show All</span></>
              )}
            </button>
            <div className="text-purple-100 text-xs sm:text-sm">
              {visibleManagers.size}/{managers.length}
            </div>
          </div>
        </div>
      </div>

      {/* Controls with mobile optimization */}
      <div className="p-3 sm:p-6 border-b border-gray-200">
        <div className={`grid gap-2 sm:gap-3 ${
          isMobile ? 'grid-cols-2' : 'grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
        }`}>
          {managers.map(manager => {
            const isVisible = visibleManagers.has(manager);
            const isHighlighted = highlightedManager === manager;
            
            return (
              <button
                key={manager}
                onClick={() => toggleManager(manager)}
                onMouseEnter={() => !isMobile && setHighlightedManager(manager)}
                onMouseLeave={() => !isMobile && setHighlightedManager(null)}
                className={`relative flex items-center space-x-2 sm:space-x-3 px-2 sm:px-4 py-2 sm:py-3 rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                  isVisible
                    ? 'border-transparent shadow-lg transform scale-105'
                    : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                } ${isHighlighted ? 'ring-2 ring-purple-300 ring-offset-2' : ''}`}
                style={{
                  backgroundColor: isVisible ? managerColors[manager] + '20' : undefined,
                  borderColor: isVisible ? managerColors[manager] : undefined
                }}
              >
                {/* Manager Avatar */}
                <div className={`relative z-10 rounded-full overflow-hidden border-2 border-white shadow-md flex-shrink-0 ${
                  isMobile ? 'w-6 h-6' : 'w-8 h-8'
                }`}>
                  <img 
                    src={`/${manager.toLowerCase()}main.png`}
                    alt={manager}
                    className="w-full h-full object-cover object-center"
                    onError={(e) => {
                      // Fallback to regular naming if main doesn't exist
                      if (e.target.src.includes('main.png')) {
                        e.target.src = `/${manager}.png`;
                      } else {
                        // Show color dot as final fallback
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<div class="w-full h-full rounded-full" style="background-color: ${managerColors[manager]}"></div>`;
                      }
                    }}
                  />
                </div>

                {/* Manager Name */}
                <span className={`relative z-10 font-medium truncate flex-1 ${
                  isMobile ? 'text-xs' : 'text-sm'
                } ${isVisible ? 'text-gray-900' : 'text-gray-600'}`}>
                  {manager}
                </span>

                {/* Score Badge */}
                {isVisible && !isMobile && (
                  <div className="relative z-10 text-xs bg-white/90 text-gray-700 px-2 py-1 rounded-full shadow-sm">
                    {chartData[chartData.length - 1]?.[manager] || 0}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart with responsive height */}
      <div className="p-3 sm:p-6">
        <div className="h-64 sm:h-80 lg:h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={chartData} 
              margin={{ 
                top: 10, 
                right: isMobile ? 10 : 30, 
                left: isMobile ? 10 : 20, 
                bottom: isMobile ? 10 : 20 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="year" 
                stroke="#64748b"
                fontSize={isMobile ? 10 : 12}
                fontWeight="500"
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? 'end' : 'middle'}
                height={isMobile ? 50 : 30}
                interval={isMobile ? 1 : 0}
              />
              <YAxis 
                stroke="#64748b"
                fontSize={isMobile ? 10 : 12}
                fontWeight="500"
                width={isMobile ? 35 : 50}
                tickFormatter={(value) => value >= 0 ? `+${value}` : `${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Zero line */}
              <Line
                dataKey={() => 0}
                stroke="#94a3b8"
                strokeWidth={isMobile ? 1.5 : 2}
                strokeDasharray="5 5"
                dot={false}
                legendType="none"
                connectNulls={false}
              />
              
              {/* Manager lines with responsive styling */}
              {managers.map(manager => (
                <Line
                  key={manager}
                  dataKey={manager}
                  stroke={managerColors[manager]}
                  strokeWidth={visibleManagers.has(manager) ? 
                    (highlightedManager === manager ? (isMobile ? 3 : 4) : (isMobile ? 2 : 3)) : 0}
                  dot={visibleManagers.has(manager) ? <CustomDot dataKey={manager} /> : false}
                  connectNulls={false}
                  strokeOpacity={visibleManagers.has(manager) ? (highlightedManager === manager ? 1 : 0.8) : 0}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default LegacyScoreOverTime;